import {
  HighlightableString,
  HighlightableStringConstructionPart,
  makeHighlightableString,
  makeHighlightableStringWithParts,
} from './HighlightableString';
import {
  HighlightableStringExpandableContainer,
  HighlightableStringExpandableContainerState,
  isHighlightableStringExpandableContainer,
} from './HighlightableStringExpandableContainer';

export interface HighlightableJson extends HighlightableStringExpandableContainer {
  originalObject: any;
  rootNode: Node;
}

export function makeHighlightableJson(o: any): HighlightableJson {
  return {
    shouldExpandForHighlighting: true,
    expandingState: HighlightableStringExpandableContainerState.Collapsed,
    originalObject: o,
    rootNode: buildNodeTree(o),
  };
}

export function isHighlightableJson(o: any): boolean {
  return isHighlightableStringExpandableContainer(o) && typeof o.rootNode !== 'undefined';
}

export enum NodeType {
  Value,
  Object,
  Array,
}

export interface Node {
  type: NodeType;
}

export interface ContainerNode extends Node, HighlightableStringExpandableContainer {}

export interface ObjectNode extends ContainerNode {
  children: Map<string, ObjectChildLink>;
}

export interface ObjectChildLink {
  keyIfNotToValueNode: HighlightableString | null | undefined;
  child: Node;
}

export interface ArrayNode extends ContainerNode {
  children: Node[];
}

export interface ValueNode extends Node {
  keyAndValue: HighlightableString | null;
}

function buildNodeTree(o: any, key?: string): Node {
  let keyPart: HighlightableStringConstructionPart | undefined;
  if (typeof key !== 'undefined') {
    keyPart = {
      content: `"${key}"`,
      cssClasses: ['jsonObjectKey'],
    };
  }

  switch (typeof o) {
    case 'undefined':
      return makeValueNodeWithNullKeyAndValue();
    case 'object':
      if (o === null) {
        return makeValueNodeWithNullKeyAndValue();
      }
      if (Array.isArray(o)) {
        const node: ArrayNode = {
          children: o.map((child) => buildNodeTree(child)),
          expandingState: HighlightableStringExpandableContainerState.ExpandedManually,
          shouldExpandForHighlighting: true,
          type: NodeType.Array,
        };
        return node;
      }
      const objectNode: ObjectNode = {
        children: new Map(),
        expandingState: HighlightableStringExpandableContainerState.ExpandedManually,
        shouldExpandForHighlighting: true,
        type: NodeType.Object,
      };

      Object.keys(o).forEach((key) => {
        const childNode = buildNodeTree(o[key], key);

        const link: ObjectChildLink = {
          keyIfNotToValueNode: childNode.type === NodeType.Value ? undefined : null,
          child: childNode,
        };
        objectNode.children.set(key, link);
      });

      return objectNode;

    case 'number':
    case 'bigint':
      return makeValueNodeWithNullKeyAndValue();
    case 'boolean':
      return makeValueNodeWithNullKeyAndValue();
    case 'string':
      return makeValueNodeWithNullKeyAndValue();
    default:
      return makeValueNodeWithNullKeyAndValue();
  }
}

export function computeKeyIfNotToValueNode(key: string): HighlightableString {
  return makeHighlightableStringWithParts({
    content: `"${key}"`,
    cssClasses: ['jsonObjectKey'],
  });
}

export function computeKeyAndValueForValueNode(
  key: string | undefined,
  value: any,
): HighlightableString {
  let keyPart: HighlightableStringConstructionPart | undefined;
  if (typeof key !== 'undefined') {
    keyPart = {
      content: `"${key}"`,
      cssClasses: ['jsonObjectKey'],
    };
  }

  switch (typeof value) {
    case 'undefined':
      return makeKeyAndValueHighlightableString(keyPart, {
        content: 'undefined',
        cssClasses: ['jsonUndefined'],
      });
    case 'object':
      if (value === null) {
        return makeKeyAndValueHighlightableString(keyPart, {
          content: 'null',
          cssClasses: ['jsonNull'],
        });
      }

      console.error('Unexpected value', key, value);
      return makeHighlightableString('UNEXPECTED VALUE');
    case 'number':
    case 'bigint':
      return makeKeyAndValueHighlightableString(keyPart, {
        content: `${value}`,
        cssClasses: ['jsonNumber'],
      });
    case 'boolean':
      return makeKeyAndValueHighlightableString(keyPart, {
        content: value ? 'true' : 'false',
        cssClasses: [value ? 'jsonBooleanTrue' : 'jsonBooleanFalse'],
      });
    case 'string':
      return makeKeyAndValueHighlightableString(keyPart, {
        content: `"${value}"`,
        cssClasses: ['jsonString'],
      });
    default:
      return makeHighlightableString(`"${value}"`);
  }
}

function makeKeyAndValueHighlightableString(
  key: HighlightableStringConstructionPart | undefined,
  value: HighlightableStringConstructionPart,
) {
  if (key) {
    return makeHighlightableStringWithParts(
      key,
      {
        content: ':',
        cssClasses: ['jsonColon'],
      },
      value,
    );
  }
  return makeHighlightableStringWithParts(value);
}

export function getKeyIfNotToValueNode(
  link: ObjectChildLink,
  key: string | undefined,
): HighlightableString | undefined {
  if (link.keyIfNotToValueNode === null) {
    if (typeof key !== 'undefined') {
      return computeKeyIfNotToValueNode(key);
    }
    return undefined;
  }
  return link.keyIfNotToValueNode;
}

export function getKeyAndValueForValueNode(
  valueNode: ValueNode,
  key: string | undefined,
  originalValue: any,
): HighlightableString {
  if (valueNode.keyAndValue === null) {
    return computeKeyAndValueForValueNode(key, originalValue);
  }
  return valueNode.keyAndValue;
}

function makeValueNode(
  key: HighlightableStringConstructionPart | undefined,
  value: HighlightableStringConstructionPart,
) {
  const node: ValueNode = {
    keyAndValue: makeKeyAndValueHighlightableString(key, value),
    type: NodeType.Value,
  };
  return node;
}

function makeValueNodeWithNullKeyAndValue(): ValueNode {
  return {
    keyAndValue: null,
    type: NodeType.Value,
  };
}
